import React, { createContext, useContext, useState, useEffect } from 'react';
import { AuthState, User, UserLike } from '../types';
import { supabase } from '../lib/supabase';

interface AuthContextType extends AuthState {
  login: (phone: string, password: string) => Promise<boolean>;
  register: (name: string, phone: string, password: string, theme: string, companyName?: string, businessContent?: string) => Promise<boolean>;
  logout: () => void;
  updateUserStamps: (userId: string, stamps: string[]) => void;
  likeUser: (likedUserId: string) => Promise<boolean>;
  hasLikedUser: (likedUserId: string) => boolean;
  updateUserProfile: (userId: string, updates: Partial<User>) => void;
  updateUserTheme: (userId: string, newTheme: string, newStartDate: string) => Promise<void>;
  allUsers: User[];
  userLikes: UserLike[];
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isLoggedIn: false,
    currentUser: null
  });
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [userLikes, setUserLikes] = useState<UserLike[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      // Load all users from database
      await loadAllUsers();
      
      // Load user likes
      await loadUserLikes();
      
      // Check if user is already logged in
      await checkLoginStatus();
      
      // Set up real-time subscriptions
      setupRealtimeSubscriptions();
    } catch (error) {
      console.error('Auth initialization failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscriptions = () => {
    // Subscribe to stamps table changes
    const stampsSubscription = supabase
      .channel('stamps_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'stamps'
        },
        async (payload) => {
          console.log('Stamps change detected:', payload);
          // Reload all users to get updated stamps
          await loadAllUsers();
        }
      )
      .subscribe();

    // Subscribe to users table changes (for likes, etc.)
    const usersSubscription = supabase
      .channel('users_changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'users'
        },
        async (payload) => {
          console.log('Users change detected:', payload);
          // Reload all users to get updated data
          await loadAllUsers();
        }
      )
      .subscribe();

    // Subscribe to user_likes table changes
    const likesSubscription = supabase
      .channel('user_likes_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_likes'
        },
        async (payload) => {
          console.log('User likes change detected:', payload);
          // Reload user likes and users
          await loadUserLikes();
          await loadAllUsers();
        }
      )
      .subscribe();

    // Cleanup function
    return () => {
      stampsSubscription.unsubscribe();
      usersSubscription.unsubscribe();
      likesSubscription.unsubscribe();
    };
  };

  const loadUserLikes = async () => {
    try {
      const { data: likes, error } = await supabase
        .from('user_likes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (likes) {
        const formattedLikes = likes.map(like => ({
          id: like.id,
          likerUserId: like.liker_user_id,
          likedUserId: like.liked_user_id,
          likeDate: like.like_date,
          createdAt: like.created_at
        }));
        setUserLikes(formattedLikes);
      }
    } catch (error) {
      console.error('Failed to load user likes:', error);
    }
  };

  const loadAllUsers = async () => {
    try {
      const { data: users, error } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) throw error;

      if (users) {
        // Load stamps for each user
        const usersWithStamps = await Promise.all(
          users.map(async (user) => {
            const { data: stamps, error: stampsError } = await supabase
              .from('stamps')
              .select('stamp_date')
              .eq('user_id', user.id)
              .order('stamp_date', { ascending: true });

            if (stampsError) {
              console.error('Error loading stamps for user:', user.id, stampsError);
              return {
                ...user,
                stamps: [],
                themeStartDate: user.theme_start_date,
                profilePhoto: user.profile_photo
              };
            }

            // Count likes for this user for today from database
            // Convert current JST date to YYYY-MM-DD format for comparison
            const now = new Date();
            const jstOffset = 9 * 60; // JST is UTC+9
            const jstDate = new Date(now.getTime() + jstOffset * 60 * 1000);
            const todayJST = jstDate.toISOString().split('T')[0];

            const { data: likesData, error: likesError } = await supabase
              .from('user_likes')
              .select('id, created_at')
              .eq('liked_user_id', user.id);

            // Filter likes by converting created_at from UTC to JST and comparing dates
            const todayLikes = likesData?.filter(like => {
              const createdAtUTC = new Date(like.created_at);
              const createdAtJST = new Date(createdAtUTC.getTime() + jstOffset * 60 * 1000);
              const createdAtJSTDate = createdAtJST.toISOString().split('T')[0];
              return createdAtJSTDate === todayJST;
            }) || [];

            const likeCount = likesError ? 0 : todayLikes.length;

            let organizationName: string | undefined = undefined;
            if (user.organization01) {
              const { data: groupData, error: groupError } = await supabase
                .from('groups')
                .select('organization_name')
                .eq('id', user.organization01)
                .maybeSingle();

              if (!groupError && groupData) {
                organizationName = groupData.organization_name;
              }
            }

            let organizationName2: string | undefined = undefined;
            if (user.organization02) {
              const { data: groupData2, error: groupError2 } = await supabase
                .from('groups')
                .select('organization_name')
                .eq('id', user.organization02)
                .maybeSingle();

              if (!groupError2 && groupData2) {
                organizationName2 = groupData2.organization_name;
              }
            }

            return {
              ...user,
              stamps: stamps?.map(s => s.stamp_date) || [],
              themeStartDate: user.theme_start_date,
              profilePhoto: user.profile_photo,
              companyName: user.company_name || undefined,
              businessContent: user.business_content || undefined,
              likes: likeCount,
              organization01: user.organization01 || undefined,
              organization02: user.organization02 || undefined,
              organizationName,
              organizationName2
            };
          })
        );

        setAllUsers(usersWithStamps);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const checkLoginStatus = async () => {
    try {
      const loginData = localStorage.getItem('loginData');
      if (loginData) {
        const { userId, loginTime } = JSON.parse(loginData);
        const hundredDaysInMs = 100 * 24 * 60 * 60 * 1000;
        
        if (Date.now() - loginTime < hundredDaysInMs) {
          // Get user from database
          const { data: user, error } = await supabase
            .from('users')
            .select('*')
            .eq('id', userId)
            .single();

          if (error) throw error;

          if (user) {
            // Load user's stamps
            const { data: stamps, error: stampsError } = await supabase
              .from('stamps')
              .select('stamp_date')
              .eq('user_id', user.id)
              .order('stamp_date', { ascending: true });

            if (stampsError) throw stampsError;

            let organizationName: string | undefined = undefined;
            if (user.organization01) {
              const { data: groupData, error: groupError } = await supabase
                .from('groups')
                .select('organization_name')
                .eq('id', user.organization01)
                .maybeSingle();

              if (!groupError && groupData) {
                organizationName = groupData.organization_name;
              }
            }

            let organizationName2: string | undefined = undefined;
            if (user.organization02) {
              const { data: groupData2, error: groupError2 } = await supabase
                .from('groups')
                .select('organization_name')
                .eq('id', user.organization02)
                .maybeSingle();

              if (!groupError2 && groupData2) {
                organizationName2 = groupData2.organization_name;
              }
            }

            const userWithStamps = {
              ...user,
              stamps: stamps?.map(s => s.stamp_date) || [],
              themeStartDate: user.theme_start_date,
              profilePhoto: user.profile_photo,
              companyName: user.company_name || undefined,
              businessContent: user.business_content || undefined,
              organization01: user.organization01 || undefined,
              organization02: user.organization02 || undefined,
              organizationName,
              organizationName2
            };

            setAuthState({ isLoggedIn: true, currentUser: userWithStamps });
          }
        } else {
          localStorage.removeItem('loginData');
        }
      }
    } catch (error) {
      console.error('Login status check failed:', error);
      localStorage.removeItem('loginData');
    }
  };

  const login = async (phone: string, password: string): Promise<boolean> => {
    try {
      // Remove hyphens from phone number for comparison
      const normalizedPhone = phone.replace(/-/g, '');
      
      const { data: user, error } = await supabase
        .from('users')
        .select('*')
        .eq('password', password)
        .limit(1);

      if (error || !user || user.length === 0) { console.log(error, user);
        return false;
      }

      // Find user with matching normalized phone number
      const foundUser = user?.find(u => 
        u.phone.replace(/-/g, '') === normalizedPhone && u.password === password
      );

      if (!foundUser) {
        return false;
      }

      // Load user's stamps
      const { data: stamps, error: stampsError } = await supabase
        .from('stamps')
        .select('stamp_date')
        .eq('user_id', foundUser.id)
        .order('stamp_date', { ascending: true });

      if (stampsError) throw stampsError;

      let organizationName: string | undefined = undefined;
      if (foundUser.organization01) {
        const { data: groupData, error: groupError } = await supabase
          .from('groups')
          .select('organization_name')
          .eq('id', foundUser.organization01)
          .maybeSingle();

        if (!groupError && groupData) {
          organizationName = groupData.organization_name;
        }
      }

      let organizationName2: string | undefined = undefined;
      if (foundUser.organization02) {
        const { data: groupData2, error: groupError2 } = await supabase
          .from('groups')
          .select('organization_name')
          .eq('id', foundUser.organization02)
          .maybeSingle();

        if (!groupError2 && groupData2) {
          organizationName2 = groupData2.organization_name;
        }
      }

      const userWithStamps = {
        ...foundUser,
        stamps: stamps?.map(s => s.stamp_date) || [],
        themeStartDate: foundUser.theme_start_date,
        profilePhoto: foundUser.profile_photo,
        companyName: foundUser.company_name,
        businessContent: foundUser.business_content,
        organization01: foundUser.organization01 || undefined,
        organization02: foundUser.organization02 || undefined,
        organizationName,
        organizationName2
      };

      setAuthState({ isLoggedIn: true, currentUser: userWithStamps });
      
      const loginData = {
        userId: foundUser.id,
        loginTime: Date.now()
      };
      localStorage.setItem('loginData', JSON.stringify(loginData));
      
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    }
  };

  const register = async (name: string, phone: string, password: string, theme: string, companyName?: string, businessContent?: string): Promise<boolean> => {
    try {
      // Remove hyphens from phone number for comparison
      const normalizedPhone = phone.replace(/-/g, '');

      // Check if phone number already exists
      const { data: existingUser, error: checkError } = await supabase
        .from('users')
        .select('id, phone')
        .limit(1);

      if (checkError) throw checkError;

      // Check for duplicate phone numbers (normalized)
      if (existingUser && existingUser.length > 0) {
        const isDuplicate = existingUser.some(user =>
          user.phone.replace(/-/g, '') === normalizedPhone
        );
        if (isDuplicate) {
          return false; // Phone number already exists
        }
      }

      // Create new user
      // Get today's date in JST (Japan Standard Time)
      const now = new Date();
      const jstOffset = 9 * 60; // JST is UTC+9
      const jstDate = new Date(now.getTime() + jstOffset * 60 * 1000);
      const today = jstDate.toISOString().split('T')[0]; // YYYY-MM-DD format in JST
      const { data: newUser, error: insertError } = await supabase
        .from('users')
        .insert({
          name,
          phone,
          password,
          theme,
          theme_start_date: today,
          likes: 0,
          company_name: companyName || null,
          business_content: businessContent || null
        })
        .select()
        .single();

      if (insertError) throw insertError;

      if (newUser) {
        let organizationName: string | undefined = undefined;
        if (newUser.organization01) {
          const { data: groupData, error: groupError } = await supabase
            .from('groups')
            .select('organization_name')
            .eq('id', newUser.organization01)
            .maybeSingle();

          if (!groupError && groupData) {
            organizationName = groupData.organization_name;
          }
        }

        let organizationName2: string | undefined = undefined;
        if (newUser.organization02) {
          const { data: groupData2, error: groupError2 } = await supabase
            .from('groups')
            .select('organization_name')
            .eq('id', newUser.organization02)
            .maybeSingle();

          if (!groupError2 && groupData2) {
            organizationName2 = groupData2.organization_name;
          }
        }

        const userWithStamps = {
          ...newUser,
          stamps: [],
          themeStartDate: newUser.theme_start_date,
          profilePhoto: newUser.profile_photo,
          companyName: newUser.company_name || undefined,
          businessContent: newUser.business_content || undefined,
          organization01: newUser.organization01 || undefined,
          organization02: newUser.organization02 || undefined,
          organizationName,
          organizationName2
        };

        setAuthState({ isLoggedIn: true, currentUser: userWithStamps });
        
        const loginData = {
          userId: newUser.id,
          loginTime: Date.now()
        };
        localStorage.setItem('loginData', JSON.stringify(loginData));
        
        // Reload all users to include the new user
        await loadAllUsers();
        
        return true;
      }

      return false;
    } catch (error) {
      console.error('Registration failed:', error);
      return false;
    }
  };

  const logout = async () => {
    setAuthState({ isLoggedIn: false, currentUser: null });
    localStorage.removeItem('loginData');
  };

  const updateUserStamps = async (userId: string, stamps: string[]) => {
    try {
      // Update local state immediately for better UX
      setAllUsers(users => 
        users.map(user => 
          user.id === userId ? { ...user, stamps } : user
        )
      );
      
      // Update current user if it's the same user
      if (authState.currentUser?.id === userId) {
        setAuthState(prev => ({
          ...prev,
          currentUser: prev.currentUser ? { ...prev.currentUser, stamps } : null
        }));
      }

      // Deduplicate stamps to avoid ON CONFLICT errors
      const uniqueStamps = Array.from(new Set(stamps));
      
      // Upsert stamps to handle duplicates gracefully
      if (uniqueStamps.length > 0) {
        const { error: upsertError } = await supabase
          .from('stamps')
          .upsert(
            uniqueStamps.map(date => ({
              user_id: userId,
              stamp_date: date
            })),
            { onConflict: 'user_id,stamp_date' }
          );

        if (upsertError) throw upsertError;
      }

      // Reload all users to ensure data consistency
      await loadAllUsers();

      // Send notification to specific user
      try {
        const targetUserId = '6075296f-e667-41e4-bb5e-a12fd9ab023b';
        const stamperUser = allUsers.find(u => u.id === userId);
        const stamperName = stamperUser?.name || 'メンバー';

        const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-push-notification`;
        const headers = {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        };

        await fetch(apiUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            targetUserId,
            title: 'スタンプが押されました',
            body: `${stamperName}さんが今日のスタンプを押しました！`,
            url: '/dashboard'
          })
        });
      } catch (notificationError) {
        console.error('Failed to send notification:', notificationError);
      }
    } catch (error) {
      console.error('Failed to update stamps:', error);
      // Revert local state on error
      await loadAllUsers();
      if (authState.currentUser?.id === userId) {
        const revertedUser = allUsers.find(user => user.id === userId);
        if (revertedUser) {
          setAuthState(prev => ({
            ...prev,
            currentUser: revertedUser
          }));
        }
      }
    }
  };

  const likeUser = async (likedUserId: string): Promise<boolean> => {
    if (!authState.currentUser) return false;

    try {
      const likedUser = allUsers.find(user => user.id === likedUserId);
      if (!likedUser) return false;

      // Get today's date in JST
      const now = new Date();
      const jstOffset = 9 * 60; // JST is UTC+9
      const jstDate = new Date(now.getTime() + jstOffset * 60 * 1000);
      const todayJST = jstDate.toISOString().split('T')[0];

      // Check if already liked this user today (using created_at converted to JST)
      const existingLike = userLikes.find(like => {
        if (like.likerUserId !== authState.currentUser!.id || like.likedUserId !== likedUserId) {
          return false;
        }
        const createdAtUTC = new Date(like.createdAt);
        const createdAtJST = new Date(createdAtUTC.getTime() + jstOffset * 60 * 1000);
        const createdAtJSTDate = createdAtJST.toISOString().split('T')[0];
        return createdAtJSTDate === todayJST;
      });

      if (existingLike) {
        return false; // Already liked today
      }

      const today = new Date().toISOString().split('T')[0];

      // Optimistically update UI first
      const newLike: UserLike = {
        id: `temp-${Date.now()}`,
        likerUserId: authState.currentUser.id,
        likedUserId: likedUserId,
        likeDate: today,
        createdAt: new Date().toISOString()
      };

      setUserLikes(prev => [...prev, newLike]);

      // Update user's like count immediately
      setAllUsers(users =>
        users.map(user =>
          user.id === likedUserId ? { ...user, likes: user.likes + 1 } : user
        )
      );

      // Insert new like
      const { error } = await supabase
        .from('user_likes')
        .insert({
          liker_user_id: authState.currentUser.id,
          liked_user_id: likedUserId,
          like_date: today
        });

      if (error) {
        // Revert optimistic updates on error
        setUserLikes(prev => prev.filter(like => like.id !== newLike.id));
        setAllUsers(users =>
          users.map(user =>
            user.id === likedUserId ? { ...user, likes: user.likes - 1 } : user
          )
        );

        // Check if the error is a unique constraint violation (user already liked)
        if (error && typeof error === 'object' && 'code' in error && error.code === '23505') {
          // User has already liked this user today, treat as success
          return true;
        }

        throw error;
      }

      return true;
    } catch (error) {
      console.error('Failed to like user:', error);
      return false;
    }
  };

  const hasLikedUser = (likedUserId: string): boolean => {
    if (!authState.currentUser) return false;

    // Get today's date in JST
    const now = new Date();
    const jstOffset = 9 * 60; // JST is UTC+9
    const jstDate = new Date(now.getTime() + jstOffset * 60 * 1000);
    const todayJST = jstDate.toISOString().split('T')[0];

    return userLikes.some(like => {
      if (like.likerUserId !== authState.currentUser!.id || like.likedUserId !== likedUserId) {
        return false;
      }
      const createdAtUTC = new Date(like.createdAt);
      const createdAtJST = new Date(createdAtUTC.getTime() + jstOffset * 60 * 1000);
      const createdAtJSTDate = createdAtJST.toISOString().split('T')[0];
      return createdAtJSTDate === todayJST;
    });
  };

  const updateUserProfile = async (userId: string, updates: Partial<User>) => {
    try {
      // Convert frontend field names to database field names
      const dbUpdates: any = {};
      if (updates.profilePhoto !== undefined) {
        dbUpdates.profile_photo = updates.profilePhoto;
      }
      if (updates.name !== undefined) {
        dbUpdates.name = updates.name;
      }
      if (updates.theme !== undefined) {
        dbUpdates.theme = updates.theme;
      }
      if (updates.themeStartDate !== undefined) {
        dbUpdates.theme_start_date = updates.themeStartDate;
      }
      if (updates.companyName !== undefined) {
        dbUpdates.company_name = updates.companyName;
      }
      if (updates.businessContent !== undefined) {
        dbUpdates.business_content = updates.businessContent;
      }
      if (updates.companyName !== undefined) {
        dbUpdates.company_name = updates.companyName;
      }
      if (updates.businessContent !== undefined) {
        dbUpdates.business_content = updates.businessContent;
      }

      const { error } = await supabase
        .from('users')
        .update(dbUpdates)
        .eq('id', userId);

      if (error) throw error;

      // Update local state
      setAllUsers(users => 
        users.map(user => 
          user.id === userId ? { ...user, ...updates } : user
        )
      );
      
      if (authState.currentUser?.id === userId) {
        setAuthState(prev => ({
          ...prev,
          currentUser: prev.currentUser ? { ...prev.currentUser, ...updates } : null
        }));
      }
    } catch (error) {
      console.error('Failed to update user profile:', error);
    }
  };

  const updateUserTheme = async (userId: string, newTheme: string, newStartDate: string) => {
    try {
      // データベースのユーザー情報を更新
      const { error: updateError } = await supabase
        .from('users')
        .update({ 
          theme: newTheme,
          theme_start_date: newStartDate
        })
        .eq('id', userId);

      if (updateError) throw updateError;

      // 既存のスタンプをすべて削除
      const { error: deleteError } = await supabase
        .from('stamps')
        .delete()
        .eq('user_id', userId);

      if (deleteError) throw deleteError;

      // ローカル状態を更新
      const updates = {
        theme: newTheme,
        themeStartDate: newStartDate,
        stamps: []
      };

      setAllUsers(users => 
        users.map(user => 
          user.id === userId ? { ...user, ...updates } : user
        )
      );
      
      if (authState.currentUser?.id === userId) {
        setAuthState(prev => ({
          ...prev,
          currentUser: prev.currentUser ? { ...prev.currentUser, ...updates } : null
        }));
      }
    } catch (error) {
      console.error('Failed to update user theme:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{
      ...authState,
      login,
      register,
      logout,
      updateUserStamps,
      likeUser,
      hasLikedUser,
      updateUserProfile,
      updateUserTheme,
      allUsers,
      userLikes,
      loading
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};